import { useCallback, useEffect, useRef, useState } from "react";
import { getTenantId } from "../../../utils/tenant";
import { getTenantTopics } from "../services/tenantTopicsService";

export const useTenantTopicsAccess = ({ enabled = true } = {}) => {
  const MISSING_TENANT_RETRIES = 5;
  const MISSING_TENANT_RETRY_DELAY_MS = 500;
  const [topicIds, setTopicIds] = useState([]);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(null);
  const missingTenantRetriesRef = useRef(0);
  const retryTimeoutRef = useRef(null);
  const isMountedRef = useRef(false);

  const clearRetryTimeout = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  const safeSetLoading = useCallback((value) => {
    if (isMountedRef.current) {
      setLoading(value);
    }
  }, []);

  const safeSetError = useCallback((value) => {
    if (isMountedRef.current) {
      setError(value);
    }
  }, []);

  const safeSetTopicIds = useCallback((value) => {
    if (isMountedRef.current) {
      setTopicIds(value);
    }
  }, []);

  const loadAccess = useCallback(async () => {
    clearRetryTimeout();

    if (!enabled) {
      missingTenantRetriesRef.current = 0;
      safeSetLoading(false);
      safeSetError(null);
      safeSetTopicIds([]);
      return [];
    }

    const tenantId = getTenantId();
    if (!tenantId) {
      if (missingTenantRetriesRef.current < MISSING_TENANT_RETRIES) {
        missingTenantRetriesRef.current += 1;
        safeSetLoading(true);
        safeSetError(null);
        retryTimeoutRef.current = setTimeout(() => {
          loadAccess();
        }, MISSING_TENANT_RETRY_DELAY_MS);
      } else {
        safeSetLoading(false);
        safeSetError("No se pudo identificar el tenant.");
        safeSetTopicIds([]);
      }
      return [];
    }

    missingTenantRetriesRef.current = 0;
    safeSetLoading(true);
    safeSetError(null);

    try {
      const response = await getTenantTopics(tenantId);
      if (!isMountedRef.current) {
        return [];
      }
      const ids = response?.topicsId;
      const normalizedIds = Array.isArray(ids)
        ? ids.map((id) => String(id || "").trim()).filter(Boolean)
        : [];

      safeSetTopicIds(normalizedIds);
      return normalizedIds;
    } catch (err) {
      safeSetError(err?.message || "No se pudieron obtener los tópicos habilitados.");
      safeSetTopicIds([]);
      return [];
    } finally {
      safeSetLoading(false);
    }
  }, [clearRetryTimeout, enabled, safeSetError, safeSetLoading, safeSetTopicIds]);

  useEffect(() => {
    isMountedRef.current = true;
    loadAccess();
    return () => {
      isMountedRef.current = false;
      clearRetryTimeout();
    };
  }, [clearRetryTimeout, loadAccess]);

  return {
    topicIds,
    loading,
    error,
    reload: loadAccess,
  };
};
